import { useId } from 'react';
import { Play } from 'lucide-react';
import { Button, SegmentedControl, TileRadioGroup } from '@ai-tutor/ui';
import { TapCheckbox } from '@/components/core/common/TapCheckbox';
import {
  BREATHING_PATTERNS,
  type BreathingPatternId,
} from './breathingPatterns';
import type { BreatheLength, BreathePrefs } from './breatheTypes';

const LENGTH_OPTIONS = [
  { value: '1', label: '1 min' },
  { value: '2', label: '2 min' },
  { value: '3', label: '3 min' },
] as const;

const PATTERN_OPTIONS = (['cyclic', 'box'] as const).map(id => ({
  value: id,
  label: BREATHING_PATTERNS[id].label,
  description: BREATHING_PATTERNS[id].why,
}));

export interface BreatheSetupProps {
  prefs: BreathePrefs;
  onChange: (prefs: BreathePrefs) => void;
  onStart: () => void;
}

/** Before a session: pick a technique and a length, then begin. */
export function BreatheSetup({ prefs, onChange, onStart }: BreatheSetupProps) {
  const soundId = useId();
  return (
    <div className='w-full max-w-md space-y-6 justify-self-center lg:justify-self-start'>
      <h2 className='text-xl font-semibold text-foreground'>
        Ready when you are
      </h2>
      <div className='space-y-3'>
        <p className='text-sm font-semibold text-foreground'>
          Choose a technique
        </p>
        <TileRadioGroup<BreathingPatternId>
          label='Choose a technique'
          labelHidden
          columns={2}
          options={PATTERN_OPTIONS}
          value={prefs.pattern}
          onChange={pattern => onChange({ ...prefs, pattern })}
        />
        <p className='text-xs text-muted-foreground'>
          {BREATHING_PATTERNS[prefs.pattern].description}
        </p>
      </div>
      <div className='space-y-2'>
        <p className='text-sm font-semibold text-foreground'>Length</p>
        <SegmentedControl
          label='Length'
          options={LENGTH_OPTIONS}
          segmentClassName='tap-target'
          value={prefs.length}
          onChange={(length: BreatheLength) => onChange({ ...prefs, length })}
        />
      </div>
      <div className='flex items-start gap-3 text-sm'>
        <TapCheckbox
          id={soundId}
          checked={prefs.sound}
          onChange={e => onChange({ ...prefs, sound: e.target.checked })}
          aria-describedby={`${soundId}-hint`}
          className='mt-0.5'
        />
        <div>
          <label
            htmlFor={soundId}
            className='block cursor-pointer font-medium text-foreground'
          >
            Soft sound at each breath
          </label>
          <p id={`${soundId}-hint`} className='text-xs text-muted-foreground'>
            Follow along with your eyes closed. Phones buzz gently too.
          </p>
        </div>
      </div>
      <div className='space-y-2'>
        <Button size='lg' className='w-full max-sm:h-11' onClick={onStart}>
          <Play className='mr-2 h-4 w-4' aria-hidden='true' />
          Start breathing
        </Button>
        <p className='hidden text-center text-xs text-muted-foreground sm:block'>
          or press{' '}
          <kbd className='rounded border border-border bg-background/70 px-1.5 py-0.5 font-mono text-xs text-foreground'>
            Space
          </kbd>
        </p>
      </div>
    </div>
  );
}
